import { Link } from "react-router-dom";
import { BadgeCheck, Target, Smile, GraduationCap, Layers, Compass, ArrowUpRight } from "lucide-react";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import CourseBentoGrid from "@/components/CourseBentoGrid";
import PhotoGallery from "@/components/PhotoGallery";
import GuaranteeSection from "@/components/GuaranteeSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import Reveal from "@/components/Reveal";
import { SITE } from "@/lib/api";

const INTRO_IMG = "/images/intro.jpg";

const FEATURES = [
  { icon: BadgeCheck, title: "Risultati garantiti", text: "La certezza del risultato, sostenuta dalla Garanzia di Profitto." },
  { icon: Target, title: "A obiettivo, non a ore", text: "Il corso finisce quando hai raggiunto l'obiettivo, non quando scadono le ore." },
  { icon: Smile, title: "Divertirsi aiuta", text: "Un clima piacevole e stimolante, perché giocando si impara meglio." },
  { icon: GraduationCap, title: "Insegnanti professionisti", text: "Docenti professionisti nelle materie, formati internamente a Scuola Sicura." },
  { icon: Layers, title: "Lezione dopo lezione", text: "Un percorso graduale per una rapida acquisizione dei concetti." },
  { icon: Compass, title: "Il tuo livello, insieme", text: "Chiedici consiglio: scopriamo insieme il tuo punto di partenza." },
];

export default function Home() {
  return (
    <main>
      <KineticHero />

      <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-intro">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Ciao e benvenuto</p>
            <h2 className="text-balance mt-4 font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
              Un nuovo tipo di insegnamento, dai risultati straordinari
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5C7178]">
              Scuola Sicura è formata da un team di professionisti esperti in grado di svolgere
              l'attività didattica in qualunque città, paese o località, con corsi pratici di
              informatica tenuti da insegnanti preparati e disponibili, capaci di istruire, stimolare
              e motivare tutti coloro che si affidano a questo nuovo tipo di insegnamento: metodico,
              ordinato e orientato al risultato.
            </p>
            <Link
              to="/chi-siamo"
              data-testid="home-intro-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1F8296] transition-colors hover:text-[#238496]"
            >
              Scopri la scuola
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-[28px] border border-[#D8E5E8] shadow-[0_20px_60px_-30px_rgba(16,51,59,0.35)]">
              <img
                src={INTRO_IMG}
                alt="Tre allievi senior sorridono davanti ai laptop durante il corso"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-features">
        <Reveal>
          <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Le caratteristiche dei nostri corsi</p>
          <h2 className="text-balance mt-4 max-w-2xl font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
            Pensati per farti raggiungere il risultato
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={0.06 * i}>
              <div className="group h-full rounded-3xl border border-[#D8E5E8] bg-white p-7 shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] transition-colors duration-500 hover:border-[#1F8296]/50">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-[#1F8296]/30 bg-[#1F8296]/10 transition-transform duration-300 group-hover:scale-110">
                  <f.icon className="h-5 w-5 text-[#1F8296]" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#1A2E33]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5C7178]">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CourseBentoGrid />
      <PhotoGallery />
      <GuaranteeSection />
      <TestimonialsSection />

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8" data-testid="home-cta">
        <Reveal>
          <div className="relative overflow-hidden rounded-[32px] border border-[#D8E5E8] bg-[#EFF6F7] px-8 py-14 text-center sm:px-14">
            <div className="bg-grid-pattern pointer-events-none absolute inset-0 opacity-50" />
            <div className="animate-float-slow pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-[#2997AB]/15 blur-3xl" />
            <div className="relative">
              <h2 className="text-balance font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
                Richiedi informazioni senza impegno
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-[#5C7178] sm:text-base">
                Per qualsiasi necessità didattica e per richiedere informazioni sui nostri corsi,
                chiamaci al <a href={`tel:${SITE.phone}`} className="font-semibold text-[#1F8296] hover:text-[#238496]">{SITE.phoneDisplay}</a>{" "}
                dalle 10.00 alle 16.00, dal lunedì al venerdì. Oppure compila il modulo: ti ricontattiamo noi.
              </p>
              <Link
                to="/contatti"
                data-testid="home-cta-contatti"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#238496]"
              >
                Contattaci <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
